import React, { useState, useEffect, useContext, createContext, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, ChevronLeft, ChevronRight, Link2 } from "lucide-react";
import { toast } from "sonner";
import {
  AI_CASE_GROUPS,
  WA_CASE_GROUPS,
  VISUAL_GRAPHIC_GROUPS,
  ANIMATION_GROUPS,
  HANDMADE_IMAGES,
  GLIMPSES,
} from "../designPortfolioData";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const ytId = (src) => (src.match(/(?:v=|embed\/|youtu\.be\/)([\w-]{6,})/) || [])[1];
const posterFor = (src) => src.replace(/\.mp4$/, ".jpg");

/* ================= Lightbox ================= */
const LightboxCtx = createContext(() => {});

function LightboxProvider({ children }) {
  const [state, setState] = useState(null); // { images, index }
  const open = useCallback((images, index) => setState({ images, index }), []);
  const close = () => setState(null);
  const step = useCallback((d) => {
    setState((s) => (s ? { ...s, index: (s.index + d + s.images.length) % s.images.length } : s));
  }, []);

  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === "Escape") setState(null);
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [state, step]);

  return (
    <LightboxCtx.Provider value={open}>
      {children}
      {createPortal(
        <AnimatePresence>
        {state && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center"
            style={{ background: "rgba(20,16,10,0.92)", backdropFilter: "blur(6px)" }}
            onClick={close}
            data-testid="lightbox-overlay"
          >
            <button onClick={close} data-testid="lightbox-close" aria-label="Close"
                    className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full flex items-center justify-center text-[#EDE7D9] border border-white/25 hover:bg-white/10 transition-colors z-10">
              <X size={20} />
            </button>
            {state.images.length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); step(-1); }} data-testid="lightbox-prev" aria-label="Previous image"
                        className="absolute left-2 md:left-6 w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center text-[#EDE7D9] border border-white/25 hover:bg-white/10 transition-colors z-10">
                  <ChevronLeft size={22} />
                </button>
                <button onClick={(e) => { e.stopPropagation(); step(1); }} data-testid="lightbox-next" aria-label="Next image"
                        className="absolute right-2 md:right-6 w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center text-[#EDE7D9] border border-white/25 hover:bg-white/10 transition-colors z-10">
                  <ChevronRight size={22} />
                </button>
              </>
            )}
            <motion.img
              key={state.index}
              src={state.images[state.index]}
              alt=""
              initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              className="max-w-[92vw] max-h-[86vh] object-contain rounded-md shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="lightbox-image"
            />
            {state.images.length > 1 && (
              <div className="absolute bottom-4 text-[#EDE7D9] text-xs tracking-[0.2em]" data-testid="lightbox-counter">
                {state.index + 1} / {state.images.length}
              </div>
            )}
          </motion.div>
        )}
        </AnimatePresence>,
        document.body
      )}
    </LightboxCtx.Provider>
  );
}

/* ---------- image with shimmer placeholder ---------- */
function ShimmerImg({ src, alt = "", className = "", imgClassName = "", onClick }) {
  const [loaded, setLoaded] = useState(false);
  const fixed = /aspect-|h-14|h-16|object-cover/.test(className);
  return (
    <span
      className={`relative block overflow-hidden ${!loaded ? "rp-shimmer" : ""} ${className}`}
      style={!loaded && !fixed ? { aspectRatio: "4 / 3" } : undefined}
      onClick={onClick}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        className={`block w-full ${fixed ? "h-full object-cover" : "h-auto"} ${imgClassName}`}
        style={{ opacity: loaded ? 1 : 0, transition: "opacity 0.4s ease" }}
      />
    </span>
  );
}

function ZoomImg({ src, group, index, alt = "", className = "" }) {
  const open = useContext(LightboxCtx);
  return (
    <ShimmerImg src={src} alt={alt}
                onClick={() => open(group, index)}
                className={`cursor-zoom-in transition-transform hover:-translate-y-0.5 ${className}`} />
  );
}

/* ---------- media outputs ---------- */
function OutputView({ output }) {
  if (output.type === "image")
    return <ZoomImg src={output.src} group={[output.src]} index={0}
                    className="w-full rounded-md border border-[#26201622] block" />;
  if (output.type === "gallery")
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {output.src.map((s, i) => (
          <ZoomImg key={i} src={s} group={output.src} index={i}
                   className="w-full rounded-md border border-[#26201622] block" />
        ))}
      </div>
    );
  if (output.type === "video")
    return (
      <video src={output.src} controls controlsList="nodownload" disablePictureInPicture onContextMenu={e => e.preventDefault()} preload="none" poster={posterFor(output.src)}
             className="w-full rounded-md border border-[#26201622] bg-black block"
             style={{ maxHeight: 520, objectFit: "contain" }} />
    );
  if (output.type === "youtube") {
    const id = ytId(output.src);
    if (!id) return null;
    return (
      <div className="w-full rounded-md overflow-hidden border border-[#26201622] bg-black" style={{ aspectRatio: "16/9" }}>
        <iframe src={`https://www.youtube.com/embed/${id}`} title="Video" loading="lazy"
                className="w-full h-full" frameBorder="0" allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
      </div>
    );
  }
  if (output.type === "link")
    return (
      <div>
        {output.preview && (
          <a href={output.src} target="_blank" rel="noreferrer noopener" aria-label="Open live site preview" className="block group/prev">
            <ShimmerImg src={output.preview} alt="Site preview"
                        className="w-full rounded-md border border-[#26201622] transition-transform group-hover/prev:-translate-y-0.5" />
          </a>
        )}
        <a href={output.src} target="_blank" rel="noreferrer noopener"
           className="inline-flex items-center gap-2 mt-4 px-6 py-3 rounded-full text-sm font-semibold text-[#F6F1E5] transition-transform hover:-translate-y-0.5"
           style={{ background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" }}>
          {output.label || "Open link →"}
        </a>
      </div>
    );
  return null;
}

/* ---------- closed-state preview thumbnails for a case item ---------- */
function thumbsFor(item) {
  const t = [];
  for (const o of item.outputs) {
    if (o.type === "image") t.push(o.src);
    else if (o.type === "gallery") t.push(...o.src.slice(0, 3));
    else if (o.type === "video") t.push(posterFor(o.src));
    else if (o.type === "youtube") {
      const id = ytId(o.src);
      if (id) t.push(`https://img.youtube.com/vi/${id}/mqdefault.jpg`);
    } else if (o.type === "link" && o.preview) t.push(o.preview);
    if (t.length >= 3) break;
  }
  return t.slice(0, 3);
}

/* ---------- case study accordion list ---------- */
function CaseList({ items, idPrefix }) {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div className="rp-foam bg-white/55 divide-y divide-[#26201618]">
      {items.map((item, i) => {
        const isOpen = openIdx === i;
        const thumbs = thumbsFor(item);
        return (
          <div key={i}>
            <button
              onClick={() => setOpenIdx(isOpen ? null : i)}
              data-testid={`${idPrefix}-item-${i}`}
              aria-expanded={isOpen}
              className="w-full text-left px-5 md:px-7 py-5 md:py-6 flex items-center gap-4 md:gap-5"
            >
              <span className="rp-display text-xl md:text-2xl font-medium text-[#262016]">{item.title}</span>
              {!isOpen && thumbs.length > 0 && (
                <span className="hidden sm:flex items-center gap-2 shrink-0">
                  {thumbs.map((src, k) => (
                    <ShimmerImg key={k} src={src}
                                className="h-14 md:h-16 w-20 md:w-24 object-cover rounded-md border border-[#26201622] bg-white/60" />
                  ))}
                </span>
              )}
              <span className="flex-1" />
              <motion.span animate={{ rotate: isOpen ? 45 : 0 }} transition={{ duration: 0.3 }}
                           className="shrink-0 text-[#B5793F]"><Plus size={18} /></motion.span>
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            style={{ overflow: "hidden" }}>
                  <div className="px-5 md:px-7 pb-7 space-y-5">
                    <div className="space-y-4">
                      {item.outputs.map((o, k) => <OutputView key={k} output={o} />)}
                    </div>
                    {item.sections?.length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 pt-1">
                        {item.sections.map((sec, k) => (
                          <div key={k} className="border-l-2 pl-4" style={{ borderColor: "#B5793F" }}>
                            <p className="text-[11px] uppercase tracking-[0.16em] text-[#8A7E6B] font-medium mb-1.5">{sec.label}</p>
                            <p className="text-sm leading-relaxed text-[#5C5042]" style={{ textWrap: "pretty" }}>{sec.text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- pill-filtered media panel (Visual/Graphic + Animation) ---------- */
function PillPanel({ groups, kind, idPrefix }) {
  const [active, setActive] = useState(groups[0].label);
  const group = groups.find((g) => g.label === active);
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6" data-testid={`${idPrefix}-pills`}>
        {groups.map((g) => {
          const isActive = g.label === active;
          return (
            <button key={g.label} onClick={() => setActive(g.label)}
                    data-testid={`${idPrefix}-pill-${slug(g.label)}`}
                    className="px-4 py-1.5 rounded-full text-[13px] font-medium transition-all"
                    style={{
                      background: isActive ? "#262016" : "transparent",
                      color: isActive ? "#EDE7D9" : "#262016",
                      border: isActive ? "1px solid #262016" : "1px solid rgba(38,32,22,0.18)",
                    }}>
              {g.label}
            </button>
          );
        })}
      </div>
      {kind === "images" ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {group.images.map((src, i) => (
            <ZoomImg key={`${active}-${i}`} src={src} group={group.images} index={i} alt={active}
                     className="w-full rounded-md border border-[#26201622] block bg-white/40" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {group.videos.map((src, i) => (
            <div key={`${active}-${i}`} className="w-full rounded-md overflow-hidden border border-[#26201622] bg-black" style={{ aspectRatio: "16/9" }}>
              <iframe src={src} title={`${active} ${i + 1}`} loading="lazy" className="w-full h-full"
                      frameBorder="0" allowFullScreen
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Glimpses of Digital Art ---------- */
function GlimpsesPanel() {
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {GLIMPSES.videos.map((src, i) => (
          <div key={i} className="w-full rounded-md overflow-hidden border border-[#26201622] bg-black" style={{ aspectRatio: "16/9" }}>
            <iframe src={src} title={`Digital art ${i + 1}`} loading="lazy" className="w-full h-full"
                    frameBorder="0" allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
          </div>
        ))}
      </div>
      <div className="rp-foam bg-white/55 p-6 md:p-8">
        <p className="rp-display text-xl md:text-2xl font-medium text-[#262016] mb-4">{GLIMPSES.uniqueAttempt.title}</p>
        {GLIMPSES.uniqueAttempt.paragraphs.map((p, i) => (
          <p key={i} className={`text-sm md:text-[15px] leading-relaxed text-[#5C5042] ${i === 0 ? "mb-3" : "italic mb-4"}`}>{p}</p>
        ))}
        <div className="w-full rounded-md overflow-hidden border border-[#26201622] bg-black" style={{ aspectRatio: "16/9" }}>
          <iframe src={GLIMPSES.uniqueAttempt.video} title="22 Hours Live Stream Art Challenge" loading="lazy"
                  className="w-full h-full" frameBorder="0" allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
        </div>
      </div>
    </div>
  );
}

/* ---------- generic sub-accordion with shareable deep links ---------- */
function SubAccordion({ sections, idPrefix, accent = "#B5793F" }) {
  const [openId, setOpenId] = useState(null);

  // Deep link: #sub-{idPrefix}-{sectionId}
  useEffect(() => {
    const applyHash = () => {
      const m = (window.location.hash || "").match(/^#sub-([a-z]+)-([a-z-]+)$/i);
      if (!m || m[1] !== idPrefix) return;
      const secId = m[2];
      if (!sections.some((s) => s.id === secId)) return;
      setOpenId(secId);
      window.setTimeout(() => {
        const el = document.querySelector(`[data-testid="${idPrefix}-sub-${secId}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 800);
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idPrefix]);

  const share = (e, secId, label) => {
    e.stopPropagation();
    const url = `${window.location.origin}${window.location.pathname}#sub-${idPrefix}-${secId}`;
    const done = () => toast.success(`Link to "${label}" copied`);
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url).then(done).catch(done);
    else done();
  };

  return (
    <div className="border-t border-[#26201618]" data-testid={`${idPrefix}-subaccordion`}>
      {sections.map((sec) => {
        const isOpen = openId === sec.id;
        return (
          <div key={sec.id} className="border-b border-[#26201618]">
            <div className="flex items-center gap-2">
              <button onClick={() => setOpenId(isOpen ? null : sec.id)}
                      data-testid={`${idPrefix}-sub-${sec.id}`}
                      aria-expanded={isOpen}
                      className="group flex-1 text-left py-4 md:py-5 flex items-center gap-4 min-w-0">
                <span className="w-1 self-stretch rounded-full transition-opacity"
                      style={{ background: accent, opacity: isOpen ? 1 : 0.35 }} />
                <span className="rp-display flex-1 text-2xl md:text-3xl font-light text-[#262016]">{sec.label}</span>
                <motion.span animate={{ rotate: isOpen ? 45 : 0 }} transition={{ duration: 0.3 }}
                             className="shrink-0 w-9 h-9 rounded-full border border-[#26201620] flex items-center justify-center group-hover:border-[#262016] transition-colors"
                             style={{ color: accent }}>
                  <Plus size={16} strokeWidth={1.7} />
                </motion.span>
              </button>
              <button onClick={(e) => share(e, sec.id, sec.label)}
                      data-testid={`${idPrefix}-share-${sec.id}`}
                      aria-label={`Copy link to ${sec.label}`}
                      title="Copy shareable link"
                      className="shrink-0 w-9 h-9 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-[#262016] hover:border-[#262016] transition-colors">
                <Link2 size={14} />
              </button>
            </div>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                            style={{ overflow: "hidden" }}>
                  <div className="pb-8 pl-5 md:pl-6">{sec.render()}</div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- grouped case list for Website / App Design ---------- */
function WebsiteAppPanel() {
  return (
    <div className="space-y-8">
      {Object.entries(WA_CASE_GROUPS).map(([group, items]) => (
        <div key={group}>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#8A7E6B] font-medium mb-3">{group}</p>
          <CaseList items={items} idPrefix={`wa-${slug(group)}`} />
        </div>
      ))}
    </div>
  );
}

/* ---------- public: AI Portfolio showcase ---------- */
export function AIPortfolioShowcase({ caseStudiesNode }) {
  const sections = [
    ...["Videos", "Applications", "Websites", "Images", "Branding"].map((label) => ({
      id: slug(label),
      label,
      render: () => <CaseList items={AI_CASE_GROUPS[label]} idPrefix={`ai-${slug(label)}`} />,
    })),
    { id: "case-studies", label: "Case Studies", render: () => caseStudiesNode },
  ];
  return (
    <LightboxProvider>
      <SubAccordion sections={sections} idPrefix="ai" accent="#19B5DE" />
    </LightboxProvider>
  );
}

/* ---------- public: Design Portfolio showcase ---------- */
export function DesignPortfolioShowcase({ caseStudiesNode }) {
  const sections = [
    { id: "visual-graphic", label: "Visual / Graphic", render: () => <PillPanel groups={VISUAL_GRAPHIC_GROUPS} kind="images" idPrefix="vg" /> },
    { id: "animation-videos", label: "Animation / Videos", render: () => <PillPanel groups={ANIMATION_GROUPS} kind="videos" idPrefix="av" /> },
    { id: "website-app", label: "Website / App Design", render: () => <WebsiteAppPanel /> },
    { id: "case-studies", label: "Case Studies", render: () => caseStudiesNode },
    { id: "glimpses", label: "Glimpses of Digital Art", render: () => <GlimpsesPanel /> },
    { id: "handmade", label: "Handmade Craft", render: () => (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {HANDMADE_IMAGES.map((src, i) => (
          <ZoomImg key={i} src={src} group={HANDMADE_IMAGES} index={i} alt={`Handmade craft ${i + 1}`}
                   className="w-full aspect-square object-cover rounded-md border border-[#26201622] block bg-white/40" />
        ))}
      </div>
    ) },
  ];
  return (
    <LightboxProvider>
      <SubAccordion sections={sections} idPrefix="design" accent="#262016" />
    </LightboxProvider>
  );
}
